import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';

import type { ColumnConfig, ScrollToColumnOptions } from '../types';

/**
 * Finds the nearest element that is horizontally scrollable.
 * Checks the element itself first, then walks up ancestors.
 * Falls back to the element itself if no scrollable ancestor is found.
 */
const findScrollableAncestor = (element: HTMLElement): HTMLElement => {
  let current: HTMLElement | null = element;
  while (current) {
    const style = getComputedStyle(current);
    const overflowX = style.overflowX;
    if (
      (overflowX === 'auto' || overflowX === 'scroll') &&
      current.scrollWidth > current.clientWidth
    ) {
      return current;
    }
    current = current.parentElement;
  }

  return element;
};

/**
 * Pure function that creates a scrollToColumn callback.
 * Exported separately for unit testing without hook wrapper.
 */
export const createScrollToColumn = (
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  tableRef: RefObject<HTMLTableElement | null>,
  columnConfigs: ColumnConfig[]
): ((columnId: string, options?: ScrollToColumnOptions) => void) => {
  return (columnId: string, options?: ScrollToColumnOptions) => {
    const containerEl = scrollContainerRef.current;
    const table = tableRef.current;
    if (!containerEl || !table) return;

    // Use the actual scrollable container (may be an ancestor when overflow-visible)
    const container = findScrollableAncestor(containerEl);

    // Check if column is pinned — if so, it's always visible, no-op
    const config = columnConfigs.find((c) => c.id === columnId);
    if (!config || config.pinned) return;

    // Find the target column header in the DOM
    const headerEl = table.querySelector<HTMLElement>(`th[data-column-id="${columnId}"]`);
    if (!headerEl) return;

    // Calculate total width of left-pinned columns
    const leftPinnedConfigs = columnConfigs.filter((c) => c.pinned === 'left');
    let pinnedWidth = 0;
    for (const pinnedConfig of leftPinnedConfigs) {
      const pinnedEl = table.querySelector<HTMLElement>(`th[data-column-id="${pinnedConfig.id}"]`);
      if (pinnedEl) pinnedWidth += pinnedEl.offsetWidth;
    }

    const { align = 'start', offset = 0 } = options ?? {};
    const colLeft = headerEl.offsetLeft;
    const colWidth = headerEl.offsetWidth;
    const scrollableWidth = container.clientWidth - pinnedWidth;

    let scrollLeft: number;

    switch (align) {
      case 'center':
        scrollLeft = colLeft - pinnedWidth - scrollableWidth / 2 + colWidth / 2;
        break;
      case 'end':
        scrollLeft = colLeft - pinnedWidth - scrollableWidth + colWidth;
        break;
      case 'start':
      default:
        scrollLeft = colLeft - pinnedWidth;
        break;
    }

    scrollLeft = Math.max(0, scrollLeft + offset);

    container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
  };
};

/**
 * Hook that returns a stable scrollToColumn callback.
 * Must be called within a component that has access to the scroll container and table refs.
 */
export const useScrollToColumn = (
  scrollContainerRef: RefObject<HTMLDivElement | null>,
  tableRef: RefObject<HTMLTableElement | null>,
  columnConfigs: ColumnConfig[]
): ((columnId: string, options?: ScrollToColumnOptions) => void) => {
  const columnConfigsRef = useRef(columnConfigs);
  columnConfigsRef.current = columnConfigs;

  return useCallback(
    (columnId: string, options?: ScrollToColumnOptions) => {
      createScrollToColumn(
        scrollContainerRef,
        tableRef,
        columnConfigsRef.current
      )(columnId, options);
    },
    [scrollContainerRef, tableRef]
  );
};
